import React from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Check } from 'lucide-react';
import { StageRail } from '../../components/ui';
import type { StageState } from '../../components/ui';
import { sbg, transition } from '../../theme/sbgTokens';

export const IMPORT_STEPS = ['Upload', 'Review', 'Import'] as const;

type StepState = 'done' | 'current' | 'upcoming';

const stateOf = (index: number, current: number): StepState =>
  index < current ? 'done' : index === current ? 'current' : 'upcoming';

/**
 * Numbered stepper (book §11.12): 28px circles joined by a 1px line. Done is
 * Blue with a white check, current a 2px Blue ring with a Navy number,
 * upcoming a strong-border ring. Below 640px it collapses to
 * "Step 2 of 3 · Review" above a slim stage rail (the screen's one angled
 * element). `current` past the last step means every step is done.
 */
const ImportStepper: React.FC<{ current: number; sx?: SxProps<Theme> }> = ({ current, sx }) => {
  const allDone = current >= IMPORT_STEPS.length;
  const mobileLabel = allDone
    ? `${IMPORT_STEPS.length} of ${IMPORT_STEPS.length} steps done · Import complete`
    : `Step ${current + 1} of ${IMPORT_STEPS.length} · ${IMPORT_STEPS[current]}`;

  return (
    <Box sx={sx}>
      <Box
        component="ol"
        aria-label="Import steps"
        sx={{ listStyle: 'none', m: 0, p: 0, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.5 }}
      >
        {IMPORT_STEPS.map((label, index) => {
          const state = stateOf(index, current);
          const last = index === IMPORT_STEPS.length - 1;
          return (
            <Box
              component="li"
              key={label}
              aria-current={state === 'current' ? 'step' : undefined}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: last ? 'none' : '0 1 168px', minWidth: 0 }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: 28,
                  height: 28,
                  flex: 'none',
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  font: 'var(--sbg-type-label-sm)',
                  fontVariantNumeric: 'tabular-nums',
                  transition: transition('background-color', 'border-color', 'color'),
                  ...(state === 'done'
                    ? { bgcolor: sbg.action.primary, color: sbg.text.onBrand }
                    : state === 'current'
                      ? { bgcolor: sbg.bg.surface, color: sbg.text.primary, boxShadow: `inset 0 0 0 2px ${sbg.action.primary}` }
                      : { bgcolor: sbg.bg.surface, color: sbg.text.secondary, boxShadow: `inset 0 0 0 1px ${sbg.border.strong}` }),
                }}
              >
                {state === 'done' ? <Check size={14} /> : index + 1}
              </Box>
              <Typography
                variant="labelMd"
                component="span"
                noWrap
                sx={{ color: state === 'upcoming' ? sbg.text.secondary : sbg.text.primary }}
              >
                {label}
                {state === 'done' && <span className="sbg-sr-only"> (done)</span>}
              </Typography>
              {!last && (
                <Box
                  aria-hidden="true"
                  sx={{
                    flex: 1,
                    minWidth: 24,
                    height: '1px',
                    bgcolor: state === 'done' ? sbg.action.primary : sbg.border.strong,
                    transition: transition('background-color'),
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: { xs: 'grid', sm: 'none' }, gap: 1 }}>
        {/* StageRail announces "Step 2 of 3: Review" itself; it says nothing once every step is done. */}
        <Typography variant="labelMd" component="p" aria-hidden={allDone ? undefined : true} sx={{ color: sbg.text.primary }}>
          {mobileLabel}
        </Typography>
        <StageRail
          aria-label="Import steps"
          stages={IMPORT_STEPS.map((label, index) => ({ label, state: stateOf(index, current) as StageState }))}
        />
      </Box>
    </Box>
  );
};

export default ImportStepper;
